/**
 * `Lower: LowerApi` (W1-04 core-ir; contract core/ir/types.ts, DESIGN.md §6.3, §6.10-§6.12, §7.3).
 *
 * The single path from the document model to the RPE IR. The same IR feeds the preview engine and the JSON
 * writer, so the preview shows exactly what is exported. Lowering is pure and deterministic; `lowerChart` builds
 * every part with one context, so it equals assembling `lowerLine` / `lowerTrack` / `lowerExtra` and its report
 * is the sum of the parts.
 *
 * Chart-level fields (lowerChart):
 *  - META: RPEVersion (the imported raw lexeme, written verbatim while it still means `pkg.rpeVersion`), its
 *    effective value, offset (ms), name, level, charter, composer, illustration (= the illustrator's name),
 *    background / song (package paths of the illustration and music assets), id (= rpeId), unknown META keys;
 *  - BPMList (raw imported triples while equal in value);
 *  - judgeLineGroup: each group at its `order`, "Default" at position 0 unless a group has order 0, unused
 *    positions below the highest order written as "" (lowerCtx.ts#groupLayout); a line's `Group` is its group's
 *    position, 0 for a line without a group;
 *  - judgeLineList in export order (lowerLine.ts), timeTags from markers, RPE-only root keys and passthrough.
 */

import type { ChartDoc, DocIndexes, RawNumberLexeme } from '../model/types';
import type { MessageRef } from '../util/types';
import { Beats } from '../time/beat';
import type { IrChart, IrMeta, IrTimeTag, LowerApi, LowerOptions, LowerReport, LowerResult } from './types';
import { createLowerCtx, reportOf, type LowerCtx } from './lowerCtx';
import { lowerBpmList, lowerExtraIr } from './lowerExtra';
import { lowerLineIr } from './lowerLine';
import { lowerTrackIr } from './lowerTrack';

/** Phira's RPEVersion fallback (F§2.2). */
export const DEFAULT_RPE_VERSION = 160;
const I32_MIN = -2147483648;
const I32_MAX = 2147483647;

/**
 * The RPEVersion Phira computes from a raw lexeme (F§2.2): a JSON float -> 160 (`as_i64` fails); a string ->
 * `parse::<i32>` of its contents (optional sign, ASCII digits, i32 range), else 160.
 */
export function effectiveRpeVersion(raw: RawNumberLexeme): number {
  if (raw.kind === 'float') return DEFAULT_RPE_VERSION;
  let text: unknown;
  try {
    text = JSON.parse(raw.lexeme);
  } catch {
    return DEFAULT_RPE_VERSION;
  }
  if (typeof text !== 'string' || !/^[+-]?[0-9]+$/.test(text)) return DEFAULT_RPE_VERSION;
  const v = Number(text);
  return Number.isSafeInteger(v) && v >= I32_MIN && v <= I32_MAX ? v : DEFAULT_RPE_VERSION;
}

function lowerMeta(ctx: LowerCtx): IrMeta {
  const { meta, pkg, timing } = ctx.doc;
  const raw = pkg.rpeVersionRaw;
  const keepRaw = raw !== null && effectiveRpeVersion(raw) === pkg.rpeVersion;
  return {
    RPEVersion: keepRaw ? raw : pkg.rpeVersion,
    rpeVersionEffective: pkg.rpeVersion,
    offset: timing.offsetMs,
    name: meta.name,
    level: meta.level,
    charter: meta.charter,
    composer: meta.composer,
    illustration: meta.illustrator,
    background: meta.illustration === null ? '' : ctx.assetPath(meta.illustration),
    song: meta.music === null ? '' : ctx.assetPath(meta.music),
    id: meta.rpeId,
    extra: meta.metaExtra,
  };
}

function lowerTimeTags(ctx: LowerCtx): IrTimeTag[] {
  return [...ctx.doc.markers.values()]
    .sort((a, b) => Beats.cmp(a.t, b.t) || a.id - b.id)
    .map((m) => ({ name: m.name, time: Beats.toTriple(m.t) }));
}

/** The chart IR (every line, in export order). */
export function lowerChartIr(ctx: LowerCtx): IrChart {
  const { doc } = ctx;
  const out: { -readonly [K in keyof IrChart]: IrChart[K] } = {
    META: lowerMeta(ctx),
    BPMList: lowerBpmList(ctx),
    judgeLineGroup: [...ctx.groups().names],
    judgeLineList: doc.lineOrder.map((id) => lowerLineIr(ctx, id)),
    rootExtra: doc.rpeRoot.extra,
  };
  if (doc.markers.size > 0) out.timeTags = lowerTimeTags(ctx);
  const root = doc.rpeRoot;
  if (root.multiLineString !== null) out.multiLineString = root.multiLineString;
  if (root.multiScale !== null) out.multiScale = root.multiScale;
  if (root.chartTime !== null) out.chartTime = root.chartTime;
  if (root.xybind !== null) out.xybind = root.xybind;
  return out;
}

function lowerChart(doc: ChartDoc, idx: DocIndexes, opts: LowerOptions): LowerResult {
  const ctx = createLowerCtx(doc, idx, opts);
  const chart = lowerChartIr(ctx);
  const extra = lowerExtraIr(ctx, chart.BPMList);
  return { chart, extra, report: reportOf(ctx.acc) };
}

/**
 * What an export changed, as messages for the export dialog (ns 'ir', catalogue core/ir/ir.messages.ts): baked
 * scroll-speed segments (count, total pieces, largest floor-position difference in px), segments whose curve Phira
 * cannot evaluate, padded editor lines, out-of-date raw beats, and holds that had to be written as constants.
 */
export function lowerReportMessages(report: LowerReport): MessageRef[] {
  const out: MessageRef[] = [];
  const finite = report.bakedSpeed.filter((b) => Number.isFinite(b.maxErrorPx));
  if (finite.length > 0) {
    const pieces = finite.reduce((sum, b) => sum + b.pieces, 0);
    const error = Math.max(...finite.map((b) => b.maxErrorPx));
    out.push({ key: 'ir.lower.bakedSpeed', params: { segments: finite.length, pieces, error: error.toFixed(2) } });
  }
  const inexact = report.bakedSpeed.length - finite.length;
  if (inexact > 0) out.push({ key: 'ir.lower.bakedSpeedInexact', params: { count: inexact } });
  if (report.paddedLines.length > 0)
    out.push({ key: 'ir.lower.paddedLines', params: { count: report.paddedLines.length } });
  if (report.staleRawTriples > 0)
    out.push({ key: 'ir.lower.staleRawTriples', params: { count: report.staleRawTriples } });
  if (report.gapsMaterialised > 0) {
    out.push({ key: 'ir.lower.gapsMaterialised', params: { count: report.gapsMaterialised } });
  }
  return out;
}

export const Lower: LowerApi = {
  lowerChart,
  lowerLine: (doc, idx, line, opts) => lowerLineIr(createLowerCtx(doc, idx, opts), line),
  lowerTrack: (doc, idx, track, opts) => lowerTrackIr(createLowerCtx(doc, idx, opts), track),
  lowerExtra: (doc, idx, opts) => lowerExtraIr(createLowerCtx(doc, idx, opts)),
};
